import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import { newsList } from "@/mocks/news";

const PAGE_SIZE = 5;

export default function NewsListPage() {
  const [keyword, setKeyword] = useState("");
  const [input, setInput] = useState("");
  const [page, setPage] = useState(1);

  const sorted = useMemo(
    () => [...newsList].sort((a, b) => b.date.localeCompare(a.date)),
    [],
  );

  const filtered = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    if (!query) return sorted;
    return sorted.filter((article) => {
      const haystack = [
        article.title,
        article.summary,
        article.category,
        article.tags.join(" "),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [keyword, sorted]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const applySearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setKeyword(input);
    setPage(1);
  };

  const reset = () => {
    setInput("");
    setKeyword("");
    setPage(1);
  };

  const featured = sorted[0];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-400" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="font-medium text-foreground-700">短剧资讯</span>
      </nav>

      <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            短剧资讯 - ai短剧
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            汇总人工智能短剧的行业动态、作品观察、创作手记与技术前沿，
            帮助观众与行业从业者了解 AI 短剧正在发生什么。
          </p>
        </div>

        <div className="lg:col-span-4 lg:flex lg:items-end">
          <form onSubmit={applySearch} className="w-full" role="search">
            <label htmlFor="news-search" className="mb-2 block text-xs font-medium text-foreground-500">
              在资讯中搜索关键词
            </label>
            <div className="flex gap-2">
              <div className="flex h-11 flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400">
                <i className="ri-search-line text-base text-foreground-400"></i>
                <input
                  id="news-search"
                  name="q"
                  type="search"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="如：科幻、配乐、出海"
                  className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                />
              </div>
              <button
                type="submit"
                className="flex h-11 items-center gap-1.5 rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-search-2-line"></i>
                <span className="whitespace-nowrap">搜索</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {featured && !keyword && (
        <Link
          to={`/news/${featured.id}`}
          className="group mt-8 block overflow-hidden rounded-lg border border-background-200 bg-background-50"
        >
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="relative h-52 overflow-hidden bg-background-200 md:h-full">
              <img
                src={featured.cover}
                alt={`${featured.title} 配图`}
                title={`${featured.title} · ai短剧资讯`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="flex flex-col justify-center p-6 md:p-10">
              <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-400">
                <span className="rounded bg-accent-500 px-2 py-0.5 font-semibold text-foreground-950">
                  头条
                </span>
                <span className="rounded bg-secondary-100 px-2 py-0.5 font-medium text-secondary-800">
                  {featured.category}
                </span>
                <span>{featured.date}</span>
              </div>
              <h2 className="mt-4 font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-2xl">
                {featured.title}
              </h2>
              <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground-500">
                {featured.summary}
              </p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                阅读全文
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
              </span>
            </div>
          </div>
        </Link>
      )}

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-500">
          共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 篇资讯
          {keyword && <span className="ml-1 text-foreground-400">（关键词：{keyword}）</span>}
        </p>
        {keyword && (
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1 text-xs font-medium text-accent-600 transition-colors hover:text-accent-700"
          >
            <i className="ri-close-line"></i>
            清除筛选
          </button>
        )}
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-5 flex flex-col gap-4">
            {pageItems.map((article) => (
              <NewsCard key={article.id} article={article} />
            ))}
          </div>
          <Pagination current={safePage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-5 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-newspaper-line text-3xl"></i>
          </span>
          <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-900">
            暂无相关的资讯内容
          </h3>
          <p className="mt-2 max-w-md text-sm text-foreground-500">
            换个关键词试试，或者重置后浏览全部短剧资讯。
          </p>
          <button
            type="button"
            onClick={reset}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-refresh-line"></i>
            重置搜索
          </button>
        </div>
      )}
    </div>
  );
}